import React, { useEffect } from "react";
import { useFinancialYearStore } from "store";

const Input = ({
  value,
  onChange,
  placeholder = "",
  label,
  showLabel = true,
  className = "",
  isDisabled = false,
  isRequired = false,
  type = "text",
  maxLength,
  helperText,
  error,
  readOnly = false,
  now = false,
  fys = false,
  fye = false,
  withinFY = false,
  name,
  ...props
}) => {
  // Get financial year dates from store
  const fysDate = useFinancialYearStore((state) => state.getFys());
  const fyeDate = useFinancialYearStore((state) => state.getFye());
  
  // Set date based on now/fys/fye props
  useEffect(() => {
    if ((now || fys || fye) && (type === "date" || type === "datetime-local") && !value && onChange) {
      let formattedDate;
      
      if (fys && fysDate) {
        // Use financial year start date
        formattedDate = fysDate;
      } else if (fye && fyeDate) {
        // Use financial year end date
        formattedDate = fyeDate;
      } else if (now) {
        // Use today's date
        const today = new Date();
        
        if (type === "date") {
          // Format: YYYY-MM-DD
          formattedDate = today.toISOString().split('T')[0];
        } else if (type === "datetime-local") {
          // Format: YYYY-MM-DDTHH:mm
          const year = today.getFullYear();
          const month = String(today.getMonth() + 1).padStart(2, '0');
          const day = String(today.getDate()).padStart(2, '0');
          const hours = String(today.getHours()).padStart(2, '0');
          const minutes = String(today.getMinutes()).padStart(2, '0');
          formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`;
        }
      }
      
      if (formattedDate) {
        // Create synthetic event to trigger onChange
        const syntheticEvent = {
          target: {
            name: name,
            value: formattedDate
          }
        };
        onChange(syntheticEvent);
      }
    }
  }, [now, fys, fye, fysDate, fyeDate, type, value, onChange, name]);

  // For checkbox type, use different rendering
  if (type === "checkbox") {
    return (
      <div className={className}>
        <label className="flex items-center">
          <input
            type="checkbox"
            name={name}
            checked={value || false}
            onChange={onChange}
            disabled={isDisabled}
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-200"
            {...props}
          />
          {label && (
            <span className="ml-2 text-sm text-gray-900">
              {label} {isRequired && <span className="text-red-500">*</span>}
            </span>
          )}
        </label>
        {helperText && (
          <p className="mt-1 text-xs text-gray-500">
            {helperText}
          </p>
        )}
        {error && (
          <p className="mt-1 text-xs text-red-600">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={className}>
      {showLabel && label && (
        <label className="block text-sm font-medium leading-6 text-gray-900">
          {label} {isRequired && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className={showLabel && label ? "mt-2" : ""}>
        {type === "textarea" ? (
          <textarea
            name={name}
            value={value || ""}
            onChange={onChange}
            placeholder={placeholder}
            disabled={isDisabled}
            readOnly={readOnly}
            maxLength={maxLength}
            className={`block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-700 disabled:ring-gray-300 read-only:bg-gray-200 read-only:text-gray-700 read-only:ring-gray-300 read-only:cursor-default`}
            {...props}
          />
        ) : (
          <input
            type={type}
            name={name}
            value={value || ""}
            onChange={onChange}
            placeholder={placeholder}
            disabled={isDisabled}
            readOnly={readOnly}
            maxLength={maxLength}
            min={withinFY && (type === "date" || type === "datetime-local") ? fysDate : props.min}
            max={withinFY && (type === "date" || type === "datetime-local") ? fyeDate : props.max}
            className={`block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-700 disabled:ring-gray-300 read-only:bg-gray-200 read-only:text-gray-700 read-only:ring-gray-300 read-only:cursor-default ${type === 'number' ? 'text-right' : ''}`}
            {...props}
          />
        )}
        {helperText && (
          <p className="mt-1 text-xs text-gray-500">
            {helperText}
          </p>
        )}
        {error && (
          <p className="mt-1 text-xs text-red-600">
            {error}
          </p>
        )}
      </div>
    </div>
  );
};

export default Input; 